import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { OkAloneService } from 'src/app/ok-alone.service';

@Component({
  selector: 'app-broadcasts-create',
  templateUrl: './broadcasts-create.component.html',
  styleUrls: ['./broadcasts-create.component.scss']
})
export class BroadcastsCreateComponent implements OnInit {

  submitted = false;
  showConfirmModal = false;
  alert: any;
  workers: any[] = [];
  saving = false;
  draftId = 0;

  formGroup = new FormGroup({
    title: new FormControl('', [Validators.required]),
    summary: new FormControl('', [Validators.required]),
    body: new FormControl('', [Validators.required]),
    priority: new FormControl('normal', [Validators.required]),
    send_all: new FormControl(true),
    on_shift_only: new FormControl(true),
    group_ids: new FormControl<number[]>([]),
    worker_ids: new FormControl<number[]>([]),
  });

  get f(): { [key: string]: AbstractControl } {
    return this.formGroup.controls;
  }

  constructor(
    public okService: OkAloneService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.okService.get('workers', { pagesize: 500, sort: 'name' }).subscribe((data) => {
      this.workers = (data.body as any[]) || [];
    });

    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (id > 0) {
      this.draftId = id;
      this.loadDraft();
    }
  }

  loadDraft(): void {
    this.okService.get('broadcasts/' + this.draftId, {}).subscribe({
      next: (data) => {
        const broadcast: any = data.body;
        if (!broadcast || broadcast.status !== 'draft') {
          // Sent broadcasts are immutable — show the read-only detail page instead.
          this.router.navigate(['/mgmt/broadcasts', this.draftId]);
          return;
        }
        this.formGroup.patchValue({
          title: broadcast.title || '',
          summary: broadcast.summary || '',
          body: broadcast.body || '',
          priority: broadcast.priority || 'normal',
          send_all: broadcast.send_all === 1 || broadcast.send_all === true,
          on_shift_only: broadcast.on_shift_only === undefined || broadcast.on_shift_only === 1 || broadcast.on_shift_only === true,
          group_ids: broadcast.group_ids || [],
          worker_ids: broadcast.worker_ids || [],
        });
      },
      error: () => {
        this.router.navigate(['/mgmt/broadcasts']);
      }
    });
  }

  saveDraft(): void {
    this.submitted = true;
    // Drafts can be partial, but need a title so they can be found in the list.
    if (!this.formGroup.value.title?.trim()) {
      this.alert = { type: 'error', content: 'A title is required to save a draft' };
      return;
    }
    this.saving = true;
    this.alert = null;
    const payload = this.buildPayload(false);
    this.persist(payload, 'Failed to save draft');
  }

  openSendConfirm(): void {
    this.submitted = true;
    if (!this.formGroup.valid) {
      return;
    }
    this.showConfirmModal = true;
  }

  confirmSend(): void {
    this.showConfirmModal = false;
    this.saving = true;
    this.alert = null;
    const payload = this.buildPayload(true);
    if (this.draftId > 0) {
      // Update the draft, then trigger the dedicated send endpoint.
      this.okService.put('broadcasts/' + this.draftId, payload).subscribe({
        next: () => {
          this.okService.post('broadcasts/' + this.draftId + '/send', {}).subscribe({
            next: () => this.router.navigate(['/mgmt/broadcasts']),
            error: (err) => this.showError(err, 'Failed to send broadcast')
          });
        },
        error: (err) => this.showError(err, 'Failed to update broadcast')
      });
      return;
    }
    this.persist(payload, 'Failed to send broadcast');
  }

  cancel(): void {
    this.router.navigate(['/mgmt/broadcasts']);
  }

  private persist(payload: any, errorMessage: string): void {
    const request = this.draftId > 0
      ? this.okService.put('broadcasts/' + this.draftId, payload)
      : this.okService.post('broadcasts', payload);

    request.subscribe({
      next: () => {
        this.router.navigate(['/mgmt/broadcasts']);
      },
      error: (err) => this.showError(err, errorMessage)
    });
  }

  private showError(err: any, fallback: string): void {
    this.alert = { type: 'error', content: err?.error?.error || err?.message || fallback };
    this.saving = false;
  }

  private buildPayload(sendNow: boolean): any {
    const value = this.formGroup.value;
    return {
      title: value.title,
      summary: value.summary,
      body: value.body,
      priority: value.priority,
      send_all: value.send_all ? 1 : 0,
      on_shift_only: value.on_shift_only ? 1 : 0,
      group_ids: value.send_all ? [] : (value.group_ids || []),
      worker_ids: value.send_all ? [] : (value.worker_ids || []),
      send_now: sendNow,
    };
  }
}
